import { splitEvery } from 'ramda';

import { readCompletedPurchases, readRejectedPurchases } from 'apis/restaurant-api/merchant-admin/purchases/api';
import { Purchase } from 'apis/restaurant-api/merchant-admin/purchases/types';

import type {
  BrandItem,
  Enterprise,
  EnterpriseBrandAPISearch,
  EnterpriseSearchResult,
  BrandSearchResult,
  BrandVenue,
  VenueRestaurantResponse,
  Brand,
  EnterpriseItem,
} from './types';
import merchantGatewayApi from '../merchant-gateway/api';
import restaurantAgent from '../restaurant-api/api';

export const MERCHANT_ENTERPRISE_SERVICE = '/merchant-enterprise-service/public/v1';

export const fetchEnterprise = async (enterpriseId: string): Promise<Enterprise> => {
  const response = await merchantGatewayApi.get(`${MERCHANT_ENTERPRISE_SERVICE}/enterprises/${enterpriseId}`);
  return response.data;
};

export const fetchEnterprisesForUser = async (): Promise<Enterprise[]> => {
  const response = await merchantGatewayApi.get(`${MERCHANT_ENTERPRISE_SERVICE}/enterprises`);
  return response.data.map((e: EnterpriseItem) => e.enterprise);
};

export const fetchEnterpriseAnalyticsDashboards = async (enterpriseId: string, locale: string): Promise<string[]> => {
  const response = await merchantGatewayApi.get(
    `${MERCHANT_ENTERPRISE_SERVICE}/enterprises/${enterpriseId}/analytics-dashboards`,
    {
      headers: {
        'Accept-Language': locale,
      },
    },
  );
  return response.data;
};

export const fetchEnterpriseVenueAvailabilityDashboards = async (
  enterpriseId: string,
  locale: string,
): Promise<string[]> => {
  const response = await merchantGatewayApi.get(
    `${MERCHANT_ENTERPRISE_SERVICE}/enterprises/${enterpriseId}/venue-availability-dashboards`,
    {
      headers: {
        'Accept-Language': locale,
      },
    },
  );
  return response.data;
};

type SearchEnterprisesAndBrandsResult = {
  enterprises: EnterpriseSearchResult[];
  brands: BrandSearchResult[];
};

export const searchEnterprisesAndBrands = async (
  searchString: string,
): Promise<SearchEnterprisesAndBrandsResult | null> => {
  const res = await merchantGatewayApi.get(`${MERCHANT_ENTERPRISE_SERVICE}/search?q=${searchString}`);
  const results: EnterpriseBrandAPISearch = res.data;
  const enterprises: EnterpriseSearchResult[] = results.enterprises.map(item => ({
    type: 'enterprise',
    value: item,
  }));

  const brands: BrandSearchResult[] = results.brands.map(item => ({
    type: 'brand',
    value: item,
  }));

  return { enterprises, brands };
};

export const fetchBrand = async (brandId: string): Promise<Brand> => {
  const response = await merchantGatewayApi.get(`${MERCHANT_ENTERPRISE_SERVICE}/brands/${brandId}`);
  return response.data;
};

export const fetchBrands = async (enterpriseId: string): Promise<BrandItem[]> => {
  const response = await merchantGatewayApi.get(`${MERCHANT_ENTERPRISE_SERVICE}/enterprises/${enterpriseId}/brands`);
  return response.data;
};

export const fetchBrandsForUser = async (): Promise<Brand[]> => {
  const response = await merchantGatewayApi.get(`${MERCHANT_ENTERPRISE_SERVICE}/brands`);
  return response.data.map((e: BrandItem) => e.brand);
};

export const fetchBrandVenues = async (brandId: string): Promise<BrandVenue[]> => {
  const response = await merchantGatewayApi.get(`${MERCHANT_ENTERPRISE_SERVICE}/brands/${brandId}/venues`);
  return response.data;
};

export const fetchBrandAnalyticsDashboards = async (brandId: string, locale: string): Promise<string[]> => {
  const response = await merchantGatewayApi.get(
    `${MERCHANT_ENTERPRISE_SERVICE}/brands/${brandId}/analytics-dashboards`,
    {
      headers: {
        'Accept-Language': locale,
      },
    },
  );
  return response.data;
};

export const fetchBrandVenueAvailaibilityDashboards = async (brandId: string, locale: string): Promise<string[]> => {
  const response = await merchantGatewayApi.get(
    `${MERCHANT_ENTERPRISE_SERVICE}/brands/${brandId}/venue-availability-dashboards`,
    {
      headers: {
        'Accept-Language': locale,
      },
    },
  );
  return response.data;
};

export const fetchVenues = async (venueIds: string[]): Promise<VenueRestaurantResponse> => {
  const idChunks = splitEvery(50, venueIds);

  const allResults = await Promise.all(
    idChunks.map(ids => restaurantAgent.get(`/v2/merchant-admin/venues/${ids.join(',')}`)),
  );

  return {
    results: allResults.flatMap(item => item.data.results),
  };
};

export const readPurchases = async (
  status: 'delivered' | 'rejected',
  payload: {
    venueId: string;
    startTime: string;
    endTime: string;
    limit: number;
    cursor?: string;
  },
  previousPurchases: Purchase[] = [],
): Promise<Purchase[]> => {
  const res = status === 'delivered' ? await readCompletedPurchases(payload) : await readRejectedPurchases(payload);
  if (!res.metadata?.next) {
    return [...previousPurchases, ...res.data.purchases];
  }
  const nextPurchases = await readPurchases(status, { ...payload, cursor: res.metadata.next }, res.data.purchases);
  return [...previousPurchases, ...nextPurchases];
};
